import { GraphQLClient } from 'graphql-request';
import * as gql from 'gql-query-builder';
import axios from 'axios';
import { MFA_ACTIVE_STATUS, KYC_APPROVED_STATUS } from '#/constants/audd';

// ── Types ──────────────────────────────────────────────────────────────────

export interface IFiatDepositProperty {
  name: string;
  value: string;
}

export type IToggleSwitch = 'on' | 'off';

export interface IPaymentsRoute {
  payment_route_id: string;
  currency_id: string;
  psp_service_id: string | null;
  name: string | null;
  crypto_network: string | null;
  crypto_network_name: string | null;
  meta: string | null;
  crypto_address_tag_type: string | null;
  verification_type: string | null;
  is_active: IToggleSwitch | null;
  is_development: IToggleSwitch | null;
  deposit_enabled: IToggleSwitch | null;
  withdrawal_enabled: IToggleSwitch | null;
  is_crypto_to_fiat: IToggleSwitch | null;
  fiat_deposit_properties: { name: string; value: string }[] | null;
}

export interface IDepositAddressResult {
  address: string;
  address_tag_type: string | null;
  address_tag_value: string | null;
}

export interface IFiatPropertyInput {
  name: string;
  value: string;
}

export interface ITravelRuleCounterParty {
  fullName: string;
  crypto_address: string;
}

export interface IWithdrawCryptoPayload {
  payment_route_id: string;
  crypto_address: string;
  amount: number;
  crypto_address_tag_type?: string;
  crypto_address_tag_value?: string;
  fees_included?: 'on' | 'off';
  comment?: string;
  mfa_token?: string;
  crypto_network?: string;
  vasp_id?: string;
  wallet_id: string;
  travel_rule_properties?: {
    counterparty: ITravelRuleCounterParty;
  };
}

export interface IVaspOption {
  id: string;
  name: string;
}

export interface IWithdrawFiatPayload {
  payment_route_id: string;
  amount: number;
  properties: IFiatPropertyInput[];
  fees_included?: 'on' | 'off';
  mfa_token?: string;
  wallet_id: string;
}

export interface IPaymentResult {
  payment_id: string;
}

export interface ICryptoAddress {
  currency_id: string;
  address: string;
  address_tag_type?: string | null;
  address_tag_value?: string | null;
  name?: string | null;
  network?: string | null;
  network_name?: string | null;
}

export interface IFiatBankAccount {
  id: string;
  bsb: string;
  accountNumber: string;
  accountName?: string;
  is_show?: 'on' | 'off';
}

const FIAT_BANK_ACCOUNT_FIELDS = ['id', 'bsb', 'accountNumber', 'accountName', 'is_show'];

// `AuddWithdrawalBankAccountItem` takes these fields and no others, and `is_show` is required
// there, so the payload is built field by field rather than passing a stored entry through.
const toBankAccountInput = (account: IFiatBankAccount) => ({
  id: account.id,
  bsb: account.bsb,
  accountNumber: account.accountNumber,
  is_show: account.is_show ?? 'on',
  accountName: account.accountName ?? null,
});

export interface IBalance {
  currency_id: string;
  total_balance: string;
  free_balance: string;
  exposed_balance: string;
}

export interface IPaymentFee {
  currency_id: string;
  payment_route_id: string | null;
  deposit_flat_fee: number;
  deposit_progressive_fee: number;
  fee_group_id: string;
  fiat_transfer_type: string;
  crypto_network: string;
}

export interface ILimitPeriod {
  limit: number;
  filled: number;
  remaining: number;
}

export interface IPaymentLimits {
  daily: ILimitPeriod;
  weekly: ILimitPeriod;
  monthly: ILimitPeriod;
}

export interface ICalculatedLimits {
  payments: IPaymentLimits;
  aggregated?: IPaymentLimits;
}

export interface IMinAmounts {
  deposit_min_amount: number;
  withdrawal_min_amount: number;
}

export interface ICurrencyMeta {
  currency_id: string;
  precision: number;
  type: string;
}

export interface IPortfolioHolding {
  currency_id: string;
  free_balance: number;
}

export interface IPortfolioWallet {
  wallet_id: string;
  name: string;
  type: string;
  holdings: IPortfolioHolding[];
}

// ── Service ────────────────────────────────────────────────────────────────

class V4Service {
  private client: GraphQLClient | null = null;
  private apiUrl: string = '';
  private token: string = '';
  private userIdRequest: Promise<string> | null = null;

  initialize(apiUrl: string) {
    this.apiUrl = apiUrl;
    this.client = new GraphQLClient(apiUrl);
  }

  setToken(token: string) {
    this.token = token;
    this.userIdRequest = null;
    if (this.client) {
      this.client.setHeader('Authorization', token); // already "Bearer <jwt>"
    }
  }

  private get gqlClient(): GraphQLClient {
    if (!this.client) throw new Error('v4Service not initialized');
    return this.client;
  }

  private get baseUrl(): string {
    return this.apiUrl.replace('/graphql', '');
  }

  private getUserId(): Promise<string> {
    if (!this.userIdRequest) {
      const query = gql.query({ operation: 'user', fields: ['user_id'] });
      this.userIdRequest = this.gqlClient
        .request(query.query, query.variables)
        .then((data: any) => data.user?.user_id)
        .catch((err) => {
          this.userIdRequest = null;
          throw err;
        });
    }
    return this.userIdRequest;
  }

  async fetchToken(apiUrl: string, storedDataKey: string): Promise<string> {
    const baseUrl = apiUrl.replace('/graphql', '');
    const resp = await axios.get(`${baseUrl}/secure/cookies`, {
      params: { cookie_key: storedDataKey },
    });
    // storeData stores { accessToken: "Bearer ..." }, retrieveData returns { cookie: { accessToken } }
    return resp.data?.cookie?.accessToken || '';
  }

  async getUserVerification(): Promise<{
    kyc_status: string | null;
    is_using_2fa: boolean;
    is_travel_rule_enabled: boolean;
    kyc_first_name: string;
    kyc_last_name: string;
    fee_group_id: string;
    has_virtual_account: boolean;
  }> {
    const query = gql.query({
      operation: 'user',
      fields: [
        'kyc_status',
        'mfa_status',
        'is_travel_rule_enabled',
        'fee_group_id',
        { kyc_data: ['kyc_property', 'kyc_value'] },
      ],
    });
    const data: any = await this.gqlClient.request(query.query, query.variables);
    const kycData: Array<{ kyc_property: string; kyc_value: string }> = data.user?.kyc_data || [];
    const getKyc = (property: string) => kycData.find((entry) => entry.kyc_property === property)?.kyc_value || '';
    return {
      kyc_status: data.user?.kyc_status || null,
      is_using_2fa: data.user?.mfa_status === MFA_ACTIVE_STATUS,
      is_travel_rule_enabled: !!data.user?.is_travel_rule_enabled,
      kyc_first_name: getKyc('first_name'),
      kyc_last_name: getKyc('last_name'),
      fee_group_id: data.user?.fee_group_id || '',
      has_virtual_account: !!getKyc('audd_va_id'),
    };
  }

  async getAllowDepositsWithoutKyc(): Promise<boolean> {
    const query = gql.query({
      operation: 'allow_deposits_without_kyc',
      fields: [],
    });
    const data: any = await this.gqlClient.request(query.query, query.variables);
    return data.allow_deposits_without_kyc === 'on';
  }

  /**
   * Every payment route configured for the currency. The widget owns method and network selection,
   * so it resolves the whole set itself instead of being handed one route: one route carries the
   * fiat leg and there is one route per crypto network, each with its own independently configured
   * fee. Routes are returned unfiltered — `is_active` is not applied server side.
   */
  async getPaymentRoutesByCurrency(currencyId: string): Promise<IPaymentsRoute[]> {
    const query = gql.query({
      operation: 'payments_routes',
      variables: {
        currency_id: { value: currencyId, required: false, type: 'String' },
        // The backend pages at 30 routes by default, which a currency can legitimately exceed
        pager: { value: { limit: 200, offset: 0 }, required: false, type: 'PagerInput' },
      },
      fields: [
        'payment_route_id',
        'currency_id',
        'psp_service_id',
        'name',
        'crypto_network',
        'crypto_network_name',
        'meta',
        'crypto_address_tag_type',
        'verification_type',
        'is_active',
        'is_development',
        'deposit_enabled',
        'withdrawal_enabled',
        'is_crypto_to_fiat',
        { fiat_deposit_properties: ['name', 'value'] },
      ],
    });
    const data: any = await this.gqlClient.request(query.query, query.variables);
    return data.payments_routes || [];
  }

  async depositAddressCrypto(currencyId: string, network: string, pspServiceId: string): Promise<IDepositAddressResult> {
    const resp = await axios.get(`${this.baseUrl}/audd/deposit-address`, {
      params: { currency_id: currencyId, network, psp_service_id: pspServiceId },
      headers: { Authorization: this.token },
    });
    return resp.data;
  }

  async createWithdrawalCrypto(payload: IWithdrawCryptoPayload): Promise<IPaymentResult> {
    const mutation = gql.mutation({
      operation: 'create_withdrawal_crypto',
      variables: {
        payment_route_id: { value: payload.payment_route_id, required: true, type: 'String' },
        crypto_address: { value: payload.crypto_address, required: true, type: 'String' },
        amount: { value: payload.amount, required: true, type: 'Float' },
        crypto_address_tag_type: { value: payload.crypto_address_tag_type, required: false, type: 'CryptoAddressTagType' },
        crypto_address_tag_value: { value: payload.crypto_address_tag_value, required: false, type: 'String' },
        fees_included: { value: payload.fees_included, required: false, type: 'ToggleSwitch' },
        comment: { value: payload.comment, required: false, type: 'String' },
        mfa_token: { value: payload.mfa_token, required: false, type: 'String' },
        crypto_network: { value: payload.crypto_network, required: false, type: 'String' },
        travel_rule_properties: { value: payload.travel_rule_properties, required: false, type: 'TravelRulePropertiesArgs' },
        vasp_id: { value: payload.vasp_id, required: false, type: 'String' },
        wallet_id: { value: payload.wallet_id, required: false, type: 'String' },
      },
      fields: ['payment_id'],
    });
    const data: any = await this.gqlClient.request(mutation.query, mutation.variables);
    return data.create_withdrawal_crypto;
  }

  async getVasps(): Promise<IVaspOption[]> {
    const query = gql.query({
      operation: 'vasps_list',
      fields: ['id', 'name'],
    });
    const data: any = await this.gqlClient.request(query.query, query.variables);
    return data.vasps_list || [];
  }

  async createWithdrawalFiat(payload: IWithdrawFiatPayload): Promise<IPaymentResult> {
    const mutation = gql.mutation({
      operation: 'create_withdrawal_fiat',
      variables: {
        payment_route_id: { value: payload.payment_route_id, required: true, type: 'String' },
        amount: { value: payload.amount, required: true, type: 'Float' },
        properties: { value: payload.properties, required: true, type: '[FiatDepositPropertyInput!]' },
        fees_included: { value: payload.fees_included, required: false, type: 'ToggleSwitch' },
        mfa_token: { value: payload.mfa_token, required: false, type: 'String' },
        wallet_id: { value: payload.wallet_id, required: false, type: 'String' },
      },
      fields: ['payment_id'],
    });
    const data: any = await this.gqlClient.request(mutation.query, mutation.variables);
    return data.create_withdrawal_fiat;
  }

  async getBalance(walletId: string | undefined, currencyId: string): Promise<IBalance | null> {
    const query = gql.query({
      operation: 'accounts_balances',
      variables: {
        wallet_id: { value: walletId, required: false, type: 'String' },
        currency_id: { value: currencyId, required: false, type: 'String' },
      },
      fields: ['currency_id', 'total_balance', 'free_balance', 'exposed_balance'],
    });
    const data: any = await this.gqlClient.request(query.query, query.variables);
    const balances: IBalance[] = data.accounts_balances || [];
    return balances.find((b) => b.currency_id === currencyId) || null;
  }

  // Registering the bank account with AUDD still goes through the KYC properties: the backend
  // creates it from them and appends it to `audd_withdrawal_bank_accounts` with a server-generated
  // id, so that list is never written from here in the same flow. `is_show` has to travel with
  // them — the backend hides the entry it creates unless the payload asks for `on`.
  async saveAuddWithdrawalBankAccount(bsb: string, accountNumber: string): Promise<void> {
    const userId = await this.getUserId();

    const mutation = gql.mutation({
      operation: 'update_kyc_user_data',
      variables: {
        user_id: { value: userId, required: true, type: 'String' },
        kyc_data: {
          value: [
            { kyc_property: 'external_bank_account_number', kyc_value: accountNumber },
            { kyc_property: 'bsb', kyc_value: bsb },
            { kyc_property: 'is_show', kyc_value: 'on' },
          ],
          required: true,
          type: '[KycUserDataEntryInput!]',
        },
      },
      fields: ['user_id', 'kyc_property', 'kyc_value', 'updated_at'],
    });
    await this.gqlClient.request(mutation.query, mutation.variables);
  }

  async estimatePaymentFee(paymentRouteId: string, type: 'deposit' | 'withdrawal', amount: number): Promise<number> {
    const mutation = gql.mutation({
      operation: 'estimate_payment_fee',
      variables: {
        payment_route_id: { value: paymentRouteId, required: true, type: 'String' },
        type: { value: type, required: true, type: 'PaymentType' },
        amount: { value: amount, required: true, type: 'Float' },
      },
      fields: [],
    });
    const data: any = await this.gqlClient.request(mutation.query, mutation.variables);
    return data.estimate_payment_fee ?? 0;
  }

  /**
   * `payments_fees` is not scoped to the caller, so it hands back a row for every fee tier. Passing
   * the user's own fee group keeps the deposit fee on screen the one they will actually be charged.
   */
  async getPaymentFees(currencyId: string, feeGroupId?: string): Promise<IPaymentFee[]> {
    const query = gql.query({
      operation: 'payments_fees',
      variables: {
        pager: { value: { limit: 100, offset: 0 }, required: false, type: 'PagerInput' },
        currency_id: { value: currencyId, required: false, type: 'String' },
        fee_group_id: { value: feeGroupId || undefined, required: false, type: 'String' },
      },
      fields: ['currency_id', 'payment_route_id', 'deposit_flat_fee', 'deposit_progressive_fee', 'fee_group_id', 'fiat_transfer_type', 'crypto_network'],
    });
    const data: any = await this.gqlClient.request(query.query, query.variables);
    return data.payments_fees || [];
  }

  async getPaymentLimits(currencyId: string, type: 'deposit' | 'withdrawal'): Promise<ICalculatedLimits | null> {
    const periodFields = [
      { daily: ['limit', 'filled', 'remaining'] },
      { weekly: ['limit', 'filled', 'remaining'] },
      { monthly: ['limit', 'filled', 'remaining'] },
    ];
    const mutation = gql.mutation({
      operation: 'calculate_payments_limits',
      variables: {
        currency_id: { value: currencyId, required: true, type: 'String' },
        type: { value: type, required: true, type: 'PaymentType' },
        notion_currency: { value: currencyId, required: true, type: 'String' },
      },
      fields: [
        { payments: periodFields },
        ...(type === 'withdrawal' ? [{ aggregated: periodFields }] : []),
      ],
    });
    const data: any = await this.gqlClient.request(mutation.query, mutation.variables);
    return data.calculate_payments_limits || null;
  }

  async getCryptoAddressBook(): Promise<ICryptoAddress[]> {
    const query = gql.query({
      operation: 'user',
      fields: [{ favorite_addresses_crypto: ['currency_id', 'address', 'address_tag_type', 'address_tag_value', 'name', 'network', 'network_name'] }],
    });
    const data: any = await this.gqlClient.request(query.query, query.variables);
    return data.user?.favorite_addresses_crypto || [];
  }

  async saveCryptoAddressBook(addresses: ICryptoAddress[]): Promise<void> {
    const mutation = gql.mutation({
      operation: 'update_user',
      variables: {
        favorite_addresses_crypto: { value: addresses, required: false, type: '[FavoriteAddressCryptoItem!]' },
      },
      fields: ['serial_id'],
    });
    await this.gqlClient.request(mutation.query, mutation.variables);
  }

  // The list is returned whole, hidden entries included, because `update_user` replaces it
  // whole — dropping the hidden ones here would delete them on the next write.
  async getFiatAddressBook(): Promise<IFiatBankAccount[]> {
    const query = gql.query({
      operation: 'user',
      fields: [{ audd_withdrawal_bank_accounts: FIAT_BANK_ACCOUNT_FIELDS }],
    });
    const data: any = await this.gqlClient.request(query.query, query.variables);
    return data.user?.audd_withdrawal_bank_accounts || [];
  }

  /**
   * Replaces the whole list. The stored list is only ever served from a read replica, so the
   * mutation is not asked to echo it back — the caller already knows what it sent.
   */
  async saveFiatAddressBook(accounts: IFiatBankAccount[]): Promise<void> {
    const mutation = gql.mutation({
      operation: 'update_user',
      variables: {
        audd_withdrawal_bank_accounts: {
          value: accounts.map(toBankAccountInput),
          required: false,
          type: '[AuddWithdrawalBankAccountItem!]',
        },
      },
      fields: ['serial_id'],
    });
    await this.gqlClient.request(mutation.query, mutation.variables);
  }

  async getMinAmounts(currencyId: string): Promise<IMinAmounts | null> {
    const query = gql.query({
      operation: 'user',
      fields: [{
        limit_group: [{
          payment_limits: ['currency_id', 'deposit_min_amount', 'withdrawal_min_amount'],
        }],
      }],
    });
    const data: any = await this.gqlClient.request(query.query, query.variables);
    const limits: any[] = data.user?.limit_group?.payment_limits || [];
    const match = limits.find((l) => l.currency_id === currencyId);
    return match
      ? { deposit_min_amount: match.deposit_min_amount, withdrawal_min_amount: match.withdrawal_min_amount }
      : null;
  }

  async getCurrencies(): Promise<ICurrencyMeta[]> {
    const query = gql.query({
      operation: 'currencies',
      variables: {
        limit: { value: 400, required: false, type: 'Int!' },
        store_mode: { value: 'on', required: false, type: 'ToggleSwitch' },
      },
      fields: ['currency_id', 'precision', 'type'],
    });
    const data: any = await this.gqlClient.request(query.query, query.variables);
    return data.currencies || [];
  }

  async getWalletsPortfolio(): Promise<IPortfolioWallet[]> {
    const query = gql.query({
      operation: 'wallets_portfolio',
      fields: [{ wallets: ['wallet_id', 'name', 'type', { holdings: ['currency_id', 'free_balance'] }] }],
    });
    const data: any = await this.gqlClient.request(query.query, query.variables);
    return data.wallets_portfolio?.wallets || [];
  }
}

export const v4Service = new V4Service();
